import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, RefreshCw, Award, AlertTriangle, Clock, X, ChevronUp, ChevronDown } from "lucide-react";
import "./TrainingStatus.css";

// TRAINING STATUS: where every enrolled fitter stands, on one screen. An attention panel over the full roster.
//
// It reads GET /api/education/admin/training-status (backend/training_status.py), which joins enrollments, lessons,
// completions, attempts and certificates at read time. Two actions, both behind a confirmation and both refused by the
// server unless the fitter is in that group:
//   * Issue certificate (Awaiting certificate only): the existing queue-certificate and approve routes. The name to
//     engrave is an editable field, filled in from the account. Approving issues the certificate and grants its one
//     email (backend/send_grants.py), which is then sent once.
//   * Nudge (Stalled only): a DRAFT email in the approval queue on the PGA / LPGA tab. Approving it there sends it,
//     once. Nothing is sent from here.
// People who do not count as fitters (staff, family, test accounts: the enrollment's counts_as_fitter) are listed and
// labelled, and left out of every count. Being kept out of the item statistics is a different thing and is not shown
// here: a former beta tester is a fitter, counted and unlabelled (Randall, 2026-10-26).

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function call(path, opts = {}) {
  const res = await fetch(`${API_URL}/api/education${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : `Request failed (${res.status})`);
  return d;
}

export function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function scoreText(correct, scored, pct) {
  if (pct === null || pct === undefined) return "";
  const p = `${Math.round(pct * 10) / 10}%`;
  return scored ? `${correct} of ${scored} (${p})` : p;
}

export function finalText(r) {
  if (r.final_state === "not sat") return "Not sat";
  const s = scoreText(r.final_correct, r.final_scored, r.final_score_pct);
  return `${r.final_state === "passed" ? "Passed" : "Failed"}${s ? `, ${s}` : ""}`;
}

// THE SORT KEY FOR EVERY COLUMN. Final sorts not sat < failed < passed, then by score.
const FINAL_RANK = { "not sat": 0, failed: 1, passed: 2 };
export const COLUMNS = [
  { key: "name", label: "Name", value: (r) => (r.name || "").toLowerCase() },
  { key: "email", label: "Email", value: (r) => (r.email || "").toLowerCase() },
  { key: "chapters", label: "Chapters passed", value: (r) => r.chapters_passed },
  { key: "final", label: "Final exam", value: (r) => FINAL_RANK[r.final_state] * 1000 + (r.final_score_pct || 0) },
  { key: "certified", label: "Certified", value: (r) => (r.certified ? `1${r.certificate?.issued_at || ""}` : "0") },
  { key: "activity", label: "Last activity", value: (r) => r.last_activity_at || "" },
];

export function sortRows(rows, key, dir) {
  const col = COLUMNS.find((c) => c.key === key) || COLUMNS[0];
  const out = [...rows].sort((a, b) => {
    const x = col.value(a);
    const y = col.value(b);
    if (x < y) return -1;
    if (x > y) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });
  return dir === "desc" ? out.reverse() : out;
}

function NotCounted({ row }) {
  if (row.counts_as_fitter !== false) return null;
  return (
    <span className="ts-excluded" title={row.counts_as_fitter_reason || "Not counted as a fitter"}>
      Not counted as a fitter
    </span>
  );
}

function Name({ row, onOpen }) {
  return (
    <button className="ts-name" onClick={() => onOpen(row.user_id)} data-testid={`ts-name-${row.user_id}`}>
      {row.name}
    </button>
  );
}

function Summary({ s }) {
  const cells = [
    { k: "enrolled", label: "Enrolled", value: s.enrolled },
    { k: "one", label: "Passed at least one chapter", ...s.passed_one_chapter },
    { k: "all", label: "Passed every chapter", ...s.passed_all_chapters },
    { k: "sat", label: "Sat the final", ...s.sat_final },
    { k: "passed", label: "Passed the final", ...s.passed_final },
    { k: "cert", label: "Certified", ...s.certified },
  ];
  return (
    <div className="ts-summary" data-testid="ts-summary">
      {cells.map((c) => (
        <div className="ts-sum-cell" key={c.k}>
          <div className="ts-sum-value">{c.value ?? c.count}</div>
          <div className="ts-sum-label">{c.label}</div>
          {c.percent !== undefined && <div className="ts-sum-pct">{c.percent}%</div>}
        </div>
      ))}
      {s.not_counted > 0 && (
        <div className="ts-sum-note">
          {s.not_counted} {s.not_counted === 1 ? "person does" : "people do"} not count as fitters (staff, family or
          test accounts): listed below, not counted here or anywhere on this page.
        </div>
      )}
    </div>
  );
}

// The heading counts what the list under it shows: the counted number, and the rows listed with it that are not fitters.
export function groupCountText(group) {
  return group.not_counted ? `${group.count} counted, ${group.not_counted} not counted` : `${group.count}`;
}

function Group({ icon, title, help, group, render, testid }) {
  return (
    <div className="ts-group" data-testid={testid}>
      <div className="ts-group-head">
        {icon}
        <span className="ts-group-title">{title}</span>
        <span className="ts-group-count">{groupCountText(group)}</span>
      </div>
      <div className="ts-group-help">{help}</div>
      {group.rows.length === 0 ? (
        <div className="ts-group-empty">Nobody.</div>
      ) : (
        <ul className="ts-group-list">
          {group.rows.map((r) => (
            <li key={r.user_id}>{render(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Thresholds({ value, defaults, onApply }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const field = (k, label) => (
    <label className="ts-th-field">
      <span>{label}</span>
      <input type="number" min="1" value={draft[k]} data-testid={`ts-th-${k}`}
             onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
    </label>
  );
  return (
    <div className="ts-thresholds">
      {field("stuck_quiz_fails", "Stuck: same chapter quiz failed")}
      {field("stuck_final_fails", "Stuck: final failed")}
      {field("stall_days", "Stalled: days without activity")}
      <button className="btn btn-secondary btn-sm" onClick={() => onApply(draft)} data-testid="ts-th-apply">Apply</button>
      <button className="btn btn-text btn-sm" onClick={() => onApply(defaults)}>
        Defaults ({defaults.stuck_quiz_fails}, {defaults.stuck_final_fails}, {defaults.stall_days})
      </button>
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="ts-modal-back" onClick={onClose}>
      <div className="ts-modal" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="ts-modal-head">
          <div className="ts-modal-title">{title}</div>
          <button className="ts-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Attempts({ rows }) {
  if (!rows.length) return <span className="ts-muted">No attempts</span>;
  return (
    <ul className="ts-attempts">
      {rows.map((a) => (
        <li key={a.attempt_id} className={a.counts ? "" : "ts-not-counted"}>
          {fmtDate(a.submitted_at || a.started_at)}: {a.submitted_at ? (a.passed ? "passed" : "failed") : "not submitted"}
          {a.submitted_at ? `, ${scoreText(a.correct_count, a.scored_count, a.score_pct)}` : ""}
          {a.voided ? " (voided, not counted)" : ""}
          {a.trial_sitting ? " (trial sitting, not counted)" : ""}
        </li>
      ))}
    </ul>
  );
}

function Detail({ d }) {
  const c = d.certificate;
  return (
    <div className="ts-detail" data-testid="ts-detail">
      <div className="ts-detail-meta">
        <div>{d.email}</div>
        <NotCounted row={d} />
        <div>Enrolled {fmtDate(d.enrolled_at)} · Last activity {fmtDate(d.last_activity_at)} · Last sign-in {fmtDate(d.last_login_at)}</div>
      </div>

      <h4>Chapters: {d.chapters_passed} of {d.chapters_total} passed</h4>
      <table className="pilot-table ts-table">
        <thead>
          <tr><th>#</th><th>Chapter</th><th>Status</th><th>Lessons</th><th>Quiz attempts</th></tr>
        </thead>
        <tbody>
          {d.chapters.map((ch) => (
            <tr key={ch.chapter_id}>
              <td>{ch.number}</td>
              <td>{ch.title}</td>
              <td className={`ts-ch-${ch.passed ? "pass" : ch.status === "failed" ? "fail" : "open"}`}>{ch.status}</td>
              <td>{ch.lessons_done} of {ch.lessons_total}</td>
              <td><Attempts rows={ch.attempts} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>Final exam: {finalText(d)}</h4>
      <Attempts rows={d.final_attempts} />

      <h4>Certificate</h4>
      {!c ? (
        <p className="ts-muted">No certificate record.</p>
      ) : (
        <table className="pilot-table ts-kv">
          <tbody>
            <tr><td>Number</td><td>{c.certificate_number}</td></tr>
            <tr><td>Holder name</td><td>{c.holder_name}{c.name_confirmed ? "" : " (not confirmed)"}</td></tr>
            <tr><td>Level</td><td>{c.level_display || c.level}</td></tr>
            <tr><td>Basis</td><td>{c.basis}</td></tr>
            <tr><td>Status</td><td>{c.issued_at ? c.status : "queued, not issued"}</td></tr>
            <tr><td>Issued</td><td>{fmtDate(c.issued_at)}</td></tr>
            <tr><td>Final score at issue</td><td>{c.final_exam_score_pct ?? "-"}%</td></tr>
            <tr><td>Pass mark at issue</td><td>{c.pass_mark_pct_at_issue ?? "-"}%</td></tr>
            <tr><td>Exam length at issue</td><td>{c.form_length_at_issue || "-"}</td></tr>
            <tr><td>Blueprint</td><td>{c.blueprint_version || "-"}</td></tr>
            <tr><td>Certificate emailed</td><td>{c.certification_emailed_at ? fmtDate(c.certification_emailed_at) : "No"}</td></tr>
            {c.revoked_at && <tr><td>Revoked</td><td>{fmtDate(c.revoked_at)}</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function engraveOk(name) {
  const n = (name || "").trim();
  return n.length > 0 && !n.includes("@");
}

function emailOutcome(e) {
  if (!e) return "No email result was returned.";
  if (e.sent) return `The certificate email was sent to ${(e.to || []).join(", ")}.`;
  if (e.reason === "already_emailed") return "The certificate email had already been sent; it was not sent again.";
  return `The certificate email was not sent: ${e.reason || "unknown reason"}.`;
}

export default function TrainingStatus() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [th, setTh] = useState(null);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [detail, setDetail] = useState(null);       // { loading, data, error }
  const [confirm, setConfirm] = useState(null);     // { kind: "issue" | "nudge", row, detail }
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [engrave, setEngrave] = useState("");       // the name to engrave, read and corrected before issue

  const qs = (t) => (t ? `?${new URLSearchParams(t)}` : "");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await call(`/admin/training-status${qs(th)}`));
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [th]);

  useEffect(() => { load(); }, [load]);

  const roster = useMemo(() => sortRows(data?.roster || [], sort.key, sort.dir), [data, sort]);

  const openDetail = async (uid) => {
    setDetail({ loading: true });
    try {
      setDetail({ data: await call(`/admin/training-status/${uid}${qs(th)}`) });
    } catch (e) {
      setDetail({ error: e.message });
    }
  };

  const askIssue = async (row) => {
    setNotice(null);
    try {
      const d = await call(`/admin/training-status/${row.user_id}`);
      setEngrave(d.certificate?.holder_name || d.name || "");
      setConfirm({ kind: "issue", row, detail: d });
    } catch (e) {
      setNotice({ tone: "bad", text: e.message });
    }
  };

  const doIssue = async () => {
    const { row, detail: d } = confirm;
    setBusy(true);
    try {
      const out = await call(`/admin/training-status/${row.user_id}/issue-certificate`, {
        method: "POST", body: JSON.stringify({ confirmed_score_pct: d.final_score_pct, holder_name: engrave.trim() }),
      });
      setNotice({
        tone: "good",
        text: `Certificate ${out.certificate_number} ${out.already ? "was already issued" : "issued"} to ${out.holder_name}. ${emailOutcome(out.email)}`,
      });
      setConfirm(null);
      await load();
    } catch (e) {
      setNotice({ tone: "bad", text: e.message });
      setConfirm(null);
    }
    setBusy(false);
  };

  const doNudge = async () => {
    const { row } = confirm;
    setBusy(true);
    try {
      const out = await call(`/admin/training-status/${row.user_id}/nudge${th ? `?stall_days=${th.stall_days}` : ""}`,
        { method: "POST" });
      setNotice({
        tone: "good",
        text: out.already
          ? `A draft nudge to ${row.name} is already waiting in the approval queue. Nothing was sent.`
          : `A draft nudge to ${row.name} (left off at ${out.left_off_at}) is in the approval queue on the PGA / LPGA tab. Nothing was sent.`,
      });
      setConfirm(null);
      await load();
    } catch (e) {
      setNotice({ tone: "bad", text: e.message });
      setConfirm(null);
    }
    setBusy(false);
  };

  if (loading && !data) {
    return <div className="pilot-empty"><Loader2 size={18} className="pilot-spin" /> Loading training status&hellip;</div>;
  }
  if (error) return <div className="pilot-empty pilot-error">{error}</div>;
  if (!data) return null;

  const a = data.attention;
  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <div className="ts" data-testid="training-status">
      <div className="ts-head">
        <div>
          <div className="section-title">Training Status</div>
          <p className="section-sub">Every enrolled fitter in the Certified Fitter course, as of {new Date(data.as_of).toLocaleString()}.</p>
        </div>
        <button className="pilot-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={13} /> {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <Summary s={data.summary} />

      {notice && (
        <div className={`ts-notice ts-notice-${notice.tone}`} role="status" data-testid="ts-notice">
          {notice.text}
          <button className="ts-icon-btn" onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      {/* ATTENTION */}
      <div className="ts-attention">
        <Group testid="ts-awaiting" icon={<Award size={15} />} title="Awaiting certificate" group={a.awaiting_certificate}
               help="Passed the final; no certificate issued."
               render={(r) => (
                 <div className="ts-row">
                   <div><Name row={r} onOpen={openDetail} /> <NotCounted row={r} /></div>
                   <div className="ts-row-sub">Final {scoreText(r.final_correct, r.final_scored, r.final_score_pct)}</div>
                   <button className="btn btn-primary btn-sm" onClick={() => askIssue(r)}
                           data-testid={`ts-issue-${r.user_id}`}>Issue certificate</button>
                 </div>
               )} />
        <Group testid="ts-stuck" icon={<AlertTriangle size={15} />} title="Stuck" group={a.stuck}
               help={`Failed the same chapter quiz ${data.thresholds.stuck_quiz_fails}+ times, or the final ${data.thresholds.stuck_final_fails}+ time${data.thresholds.stuck_final_fails === 1 ? "" : "s"}, and not passed since.`}
               render={(r) => (
                 <div className="ts-row">
                   <div><Name row={r} onOpen={openDetail} /> <NotCounted row={r} /></div>
                   {r.stuck_on.map((s) => <div className="ts-row-sub" key={s.label}>{s.label}</div>)}
                 </div>
               )} />
        <Group testid="ts-stalled" icon={<Clock size={15} />} title="Stalled" group={a.stalled}
               help={`Not certified, no course activity in ${data.thresholds.stall_days} days.`}
               render={(r) => (
                 <div className="ts-row">
                   <div><Name row={r} onOpen={openDetail} /> <NotCounted row={r} /></div>
                   <div className="ts-row-sub">
                     {r.days_since_activity} days {r.last_activity_at ? "since last activity" : "since enrolling, never started"}
                     {r.left_off_at ? ` · left off at ${r.left_off_at}` : ""}
                   </div>
                   {r.nudge_waiting ? (
                     <span className="ts-muted">Draft nudge waiting in the approval queue</span>
                   ) : (
                     <button className="btn btn-secondary btn-sm" onClick={() => setConfirm({ kind: "nudge", row: r })}
                             data-testid={`ts-nudge-${r.user_id}`}>Nudge</button>
                   )}
                 </div>
               )} />
      </div>

      <Thresholds value={data.thresholds} defaults={data.defaults} onApply={(t) => setTh({ ...t })} />

      {/* ROSTER */}
      <div className="ts-roster">
        <table className="pilot-table ts-table" data-testid="ts-roster">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key}>
                  <button className="ts-sort" onClick={() => sortBy(c.key)} data-testid={`ts-sort-${c.key}`}>
                    {c.label}
                    {sort.key === c.key && (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roster.map((r) => (
              <tr key={r.user_id} className={r.counts_as_fitter === false ? "ts-row-excluded" : ""}>
                <td><Name row={r} onOpen={openDetail} /> <NotCounted row={r} /></td>
                <td className="pilot-email">{r.email}</td>
                <td>{r.chapters_passed} of {r.chapters_total}</td>
                <td>{finalText(r)}</td>
                <td>{r.certified ? `Yes, ${r.certificate.certificate_number}, ${fmtDate(r.certificate.issued_at)}` : "No"}</td>
                <td>{fmtDate(r.last_activity_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <Modal title={detail.data ? detail.data.name : "Fitter"} onClose={() => setDetail(null)} testid="ts-detail-modal">
          {detail.loading && <div className="pilot-empty"><Loader2 size={18} className="pilot-spin" /> Loading&hellip;</div>}
          {detail.error && <div className="pilot-empty pilot-error">{detail.error}</div>}
          {detail.data && <Detail d={detail.data} />}
        </Modal>
      )}

      {confirm?.kind === "issue" && (
        <Modal title="Issue certificate" onClose={() => !busy && setConfirm(null)} testid="ts-confirm-issue">
          {confirm.detail.issue_ready ? (
            <>
              <p>
                Issue the Certified Fitter certificate to <b>{confirm.detail.name}</b>, final exam{" "}
                <b>{scoreText(confirm.detail.final_correct, confirm.detail.final_scored, confirm.detail.final_score_pct)}</b>?
              </p>
              <label className="ts-field">
                Name to engrave
                <input type="text" value={engrave} onChange={(e) => setEngrave(e.target.value)} disabled={busy}
                       maxLength={120} data-testid="ts-engrave-name" />
              </label>
              <p className="ts-muted">
                Filled in from the account. Read it and correct it if it is wrong: it is engraved exactly as written
                here and cannot be changed after issue. Issuing also approves the certificate email to{" "}
                {confirm.detail.email}, which is sent once.
              </p>
              <div className="ts-modal-actions">
                <button className="btn btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
                <button className="btn btn-primary" onClick={doIssue} disabled={busy || !engraveOk(engrave)}
                        data-testid="ts-confirm-issue-go">
                  {busy ? "Issuing…" : "Issue certificate"}
                </button>
              </div>
            </>
          ) : (
            <>
              <p>{confirm.detail.name} passed the final but cannot be issued a certificate yet.</p>
              <p className="ts-muted">{confirm.detail.issue_blocked_reason}</p>
              <div className="ts-modal-actions">
                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Close</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {confirm?.kind === "nudge" && (
        <Modal title="Nudge" onClose={() => !busy && setConfirm(null)} testid="ts-confirm-nudge">
          <p>
            Write a draft nudge to <b>{confirm.row.name}</b>, naming where they left off
            ({confirm.row.left_off_at || "the course"}), into the approval queue?
          </p>
          <p className="ts-muted">
            Nothing is sent yet. The draft waits in the approval queue on the PGA / LPGA tab, and approving it there
            sends it, once.
          </p>
          <div className="ts-modal-actions">
            <button className="btn btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={doNudge} disabled={busy} data-testid="ts-confirm-nudge-go">
              {busy ? "Writing…" : "Write the draft"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
